import clsx from 'clsx';
import type { CalendarItem } from '../../api/organizer';
import { dayHeading } from '../../utils/calendarGrid';
import { chipClasses, taskIdOf } from './calendarUtils';

interface DayDetailPanelProps {
  day: string;
  items: CalendarItem[];
  isToday: boolean;
  onOpenTask: (taskId: string) => void;
  onClose: () => void;
}

/**
 * One day as an agenda.
 *
 * This is the agenda view for v1 — a separate week view would need an hour gutter
 * and overlap packing, and the month grid plus this panel already answers "what
 * does my day look like".
 */
export function DayDetailPanel({ day, items, isToday, onOpenTask, onClose }: DayDetailPanelProps) {
  const allDay = items.filter((i) => i.allDay);
  const timed = items.filter((i) => !i.allDay);

  return (
    <div className="rounded-xl border border-border-light bg-surface-primary p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-body-sm font-medium text-text-primary">
          {dayHeading(day)}
          {isToday && <span className="ml-2 text-body-xs font-normal text-accent-dark">Today</span>}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-body-xs text-text-tertiary hover:text-text-primary"
        >
          Close
        </button>
      </div>

      {items.length === 0 ? (
        <p className="py-4 text-center text-body-xs text-text-muted">Nothing on this day.</p>
      ) : (
        <div className="space-y-3">
          {allDay.length > 0 && (
            <ul className="space-y-1.5">
              {allDay.map((item) => (
                <Row key={item.id} item={item} onOpenTask={onOpenTask} />
              ))}
            </ul>
          )}

          {timed.length > 0 && (
            <ul className="space-y-1.5">
              {timed.map((item) => (
                <Row key={item.id} item={item} onOpenTask={onOpenTask} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  item,
  onOpenTask,
}: {
  item: CalendarItem;
  onOpenTask: (taskId: string) => void;
}) {
  const taskId = taskIdOf(item);
  const meeting = item.meta.kind === 'meeting' ? item.meta : null;

  const body = (
    <div className={clsx('flex-1 rounded-lg px-2.5 py-2', chipClasses(item))}>
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 text-body-sm">{item.title}</span>
        {item.kind === 'reminder' && <span className="shrink-0 text-body-xs">reminder</span>}
      </div>
      {meeting && (meeting.location || meeting.attendeeCount > 0) && (
        <p className="mt-0.5 text-body-xs opacity-80">
          {[
            meeting.location,
            meeting.attendeeCount > 0
              ? `${meeting.attendeeCount} ${meeting.attendeeCount === 1 ? 'person' : 'people'}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </div>
  );

  return (
    <li className="flex items-start gap-3">
      <span className="w-12 shrink-0 pt-2 text-right text-body-xs tabular-nums text-text-muted">
        {item.startTimeLabel ?? 'all day'}
      </span>

      {taskId ? (
        <button type="button" onClick={() => onOpenTask(taskId)} className="flex flex-1 text-left">
          {body}
        </button>
      ) : item.href ? (
        // Meetings link out to Google. Never a mutation — the calendar is read-only.
        <a href={item.href} target="_blank" rel="noopener noreferrer" className="flex flex-1">
          {body}
        </a>
      ) : (
        <div className="flex flex-1">{body}</div>
      )}

      {meeting?.conferenceUrl && (
        <a
          href={meeting.conferenceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 pt-2 text-body-xs font-medium text-accent-dark hover:underline"
        >
          Join
        </a>
      )}
    </li>
  );
}
